import {
  type QueryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import { type ApiError } from '../../../../utils/api/ApiError/ApiError';
import { fetcher } from '../../../../utils/api/fetcher/fetcher';

// Define the zod schema for AssetFamilyTaxonomy
export const assetFamilyTaxonomyShape = z.object({
  uuid: z.string(),
  name: z.string(),
});

// Define the zod schema for LexileLevelDto
export const lexileLevelShape = z.object({
  lexileValue: z.number(),
  lexilePrefix: z.string().optional(),
});

// Define the zod schema for AssetFamilyLexileRange
export const assetFamilyLexileRangeShape = z.object({
  min: z.string(),
  max: z.string(),
});

// Define the zod schema for AssetFamilyAsset
export const assetFamilyAssetShape = z
  .object({
    pcmId: z.string(),
    title: z.string(),
    language: z.string().optional(),
    assetType: z.string(),
    assetTypeLabel: z.string(),
    contentType: z.string(),
    contentTypeLabel: z.string(),
    assetUriReference: z.string(),
    textLevels: lexileLevelShape.optional(),
    assetFamilyId: z.string().optional(),
  })
  .passthrough(); // Allow additional properties

// Define the zod schema for AssetFamilyRecommendedAsset
export const assetFamilyRecommendedAssetShape = z
  .object({
    pcmId: z.string(),
    title: z.string(),
    language: z.string().optional(),
    assetType: z.string(),
    assetTypeLabel: z.string(),
    contentType: z.string(),
    contentTypeLabel: z.string(),
    textLevels: lexileLevelShape.optional(),
  })
  .passthrough(); // Allow additional properties

// Define the zod schema for CondensedAssetFamilyDto
export const condensedAssetFamilyShape = z
  .object({
    assetFamilyId: z.string().optional(),
    title: z.string().optional(),
  })
  .passthrough(); // Allow additional properties

// Define the zod schema for AssetFamilyRelated
export const assetFamilyRelatedShape = z.object({
  prerequisites: z.array(assetFamilyAssetShape).optional(),
  corequisites: z.array(assetFamilyAssetShape).optional(),
  postrequisites: z.array(assetFamilyAssetShape).optional(),
  lessonPlan: assetFamilyAssetShape.optional(),
  professionalDevelopment: assetFamilyAssetShape.optional(),
});

// Define the zod schema for AssetFamilyAlternate
export const assetFamilyAlternateShape = z.object({
  lexiles: z.array(assetFamilyAssetShape).optional(),
  languages: z.array(assetFamilyAssetShape).optional(),
  readAloud: assetFamilyAssetShape.optional(),
});

// Define the zod schema for AssetFamilyRelationships
export const assetFamilyRelationshipsShape = z.object({
  peers: z.array(condensedAssetFamilyShape).optional(),
  alternate: assetFamilyAlternateShape.optional(),
  originalReferences: z.array(assetFamilyAssetShape).optional(),
  related: assetFamilyRelatedShape.optional(),
  recommendations: z.array(assetFamilyRecommendedAssetShape).optional(),
});

// Define the zod schema for AssetFamilyDetails
export const assetFamilyDetailsShape = z.object({
  lexileRange: assetFamilyLexileRangeShape.optional(),
  standards: z.array(assetFamilyTaxonomyShape).optional(),
  skills: z.array(assetFamilyTaxonomyShape).optional(),
  topics: z.array(assetFamilyTaxonomyShape).optional(),
  subjects: z.array(assetFamilyTaxonomyShape).optional(),
});

// Define the zod schema for AssetFamilyDto
export const assetFamilyShape = z.object({
  uuid: z.string(),
  title: z.string(),
  description: z.string().optional(),
  anchor: assetFamilyAssetShape,
  relationships: assetFamilyRelationshipsShape,
  details: assetFamilyDetailsShape.optional(),
});

export type AssetFamily = z.infer<typeof assetFamilyShape>;
export type AssetFamilyAsset = z.infer<typeof assetFamilyAssetShape>;
export type AssetFamilyTaxonomy = z.infer<typeof assetFamilyTaxonomyShape>;

export type GetAssetFamilyApiParams = {
  assetFamilyId: string;
};

async function getAssetFamilyApi({ assetFamilyId }: GetAssetFamilyApiParams) {
  const assetFamily = await fetcher(`/asset-families/${assetFamilyId}`, {
    service: '/composite',
    version: '/v1',
  });

  return assetFamilyShape.parse(assetFamily);
}

export const getAssetFamilyApiQueryKey = (args?: GetAssetFamilyApiParams) => {
  const { assetFamilyId } = args ?? {};

  return ['/v1', 'asset-families', assetFamilyId].filter(Boolean) as string[];
};

export function useGetAssetFamilyApi(
  args: GetAssetFamilyApiParams,
  customQueryOptions?: QueryOptions<AssetFamily, ApiError>
): UseQueryResult<AssetFamily, ApiError> {
  return useQuery({
    queryKey: getAssetFamilyApiQueryKey(args),
    queryFn: () => getAssetFamilyApi(args),
    enabled: !!args.assetFamilyId,
    staleTime: 90000, // Match the original staleTime
    ...customQueryOptions,
  });
}
